(function () {
  'use strict';

  /* ─── Flavor-pill sync ─────────────────────────────────────────────── */

  var galleryImageCache = window.gigiGalleryImageCache || (window.gigiGalleryImageCache = new Map());
  var galleryImagesReady = window.gigiGalleryImagesReady || (window.gigiGalleryImagesReady = new Set());
  var GFS_GALLERY_SLIDE_MS = 420;
  var GFS_DESKTOP_QUERY = '(min-width: 750px)';
  var GFS_SWIPE_MIN_DISTANCE = 42;
  var GFS_SWIPE_AXIS_RATIO = 1.25;
  var GFS_SWIPE_START_DISTANCE = 8;

  function gfsPrefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getGallerySlideDirection(prevIndex, nextIndex, count) {
    if (prevIndex === nextIndex) return 0;
    if (nextIndex === prevIndex + 1) return 1;
    if (nextIndex === prevIndex - 1) return -1;
    if (prevIndex === count - 1 && nextIndex === 0) return 1;
    if (prevIndex === 0 && nextIndex === count - 1) return -1;
    return nextIndex > prevIndex ? 1 : -1;
  }

  function preloadGalleryImage(src) {
    if (!src) return Promise.resolve();

    if (galleryImagesReady.has(src)) {
      return Promise.resolve();
    }

    if (galleryImageCache.has(src)) {
      return galleryImageCache.get(src);
    }

    var promise = new Promise(function (resolve) {
      var img = new Image();
      img.decoding = 'async';

      img.onload = function () {
        galleryImagesReady.add(src);
        resolve(img);
      };

      img.onerror = function () {
        resolve(img);
      };

      img.src = src;
    });

    galleryImageCache.set(src, promise);
    return promise;
  }

  function applyGalleryImage(mainImage, thumb, fullSrc) {
    mainImage.removeAttribute('srcset');
    mainImage.removeAttribute('sizes');
    mainImage.src = fullSrc;
    mainImage.dataset.mediaSrc = fullSrc;

    if (thumb.dataset.alt) {
      mainImage.alt = thumb.dataset.alt;
    }

    mainImage.classList.remove('gfs-panel__main-img--loading');
    var stage = mainImage.closest('[data-gfs-gallery-stage]');
    if (stage) stage.classList.add('is-gfs-image-loaded');
  }

  function markGalleryStageImageState(stage) {
    if (!stage) return;

    var image = stage.querySelector('.gfs-panel__main-img:not(.gfs-panel__main-img--slide-incoming)');
    if (!image) return;

    function markLoaded() {
      stage.classList.add('is-gfs-image-loaded');
    }

    if (image.complete && image.naturalWidth > 0) {
      markLoaded();
      return;
    }

    image.addEventListener('load', markLoaded, { once: true });
    image.addEventListener('error', markLoaded, { once: true });
  }

  function slideGalleryImage(gallery, mainImage, thumb, fullSrc, direction) {
    if (direction === 0 || gfsPrefersReducedMotion()) {
      applyGalleryImage(mainImage, thumb, fullSrc);
      return;
    }

    var stage = gallery.querySelector('[data-gfs-gallery-stage]');
    if (!stage || gallery.dataset.gfsGallerySliding === 'true') {
      applyGalleryImage(mainImage, thumb, fullSrc);
      return;
    }

    gallery.dataset.gfsGallerySliding = 'true';

    var imageRect = mainImage.getBoundingClientRect();
    stage.style.width = imageRect.width + 'px';
    stage.style.height = imageRect.height + 'px';

    var outgoing = mainImage;
    outgoing.classList.add('gfs-panel__main-img--slide');
    outgoing.style.transform = 'translateX(0)';

    var incoming = document.createElement('img');
    incoming.className = 'gfs-panel__main-img gfs-panel__main-img--slide gfs-panel__main-img--slide-incoming';
    incoming.src = fullSrc;
    incoming.alt = thumb.dataset.alt || outgoing.alt || '';
    incoming.dataset.mediaSrc = fullSrc;
    if (outgoing.width) incoming.width = outgoing.width;
    if (outgoing.height) incoming.height = outgoing.height;
    incoming.loading = 'eager';
    incoming.style.transform = 'translateX(' + direction * 100 + '%)';

    stage.appendChild(incoming);
    incoming.offsetHeight;

    requestAnimationFrame(function () {
      outgoing.style.transform = 'translateX(' + direction * -100 + '%)';
      incoming.style.transform = 'translateX(0)';
    });

    var finished = false;

    function finishSlide() {
      if (finished) return;
      finished = true;

      incoming.classList.remove('gfs-panel__main-img--slide', 'gfs-panel__main-img--slide-incoming');
      incoming.style.transform = '';
      incoming.style.transition = '';
      outgoing.remove();
      stage.style.width = '';
      stage.style.height = '';
      delete gallery.dataset.gfsGallerySliding;
      applyGalleryImage(incoming, thumb, fullSrc);
    }

    incoming.addEventListener('transitionend', finishSlide, { once: true });
    window.setTimeout(finishSlide, GFS_GALLERY_SLIDE_MS + 60);
  }

  function markGalleryThumbs(gallery, activeThumb) {
    gallery.querySelectorAll('[data-gfs-gallery-thumb]').forEach(function (button) {
      button.setAttribute('aria-current', button === activeThumb ? 'true' : 'false');
    });

    var thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
    var index = Array.prototype.indexOf.call(thumbs, activeThumb);

    gallery.dispatchEvent(
      new CustomEvent('gigiGallerySlideChanged', {
        detail: { index: index >= 0 ? index : 0 },
      })
    );
  }

  function getActiveGalleryThumbIndex(gallery) {
    var thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
    var activeThumb = gallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
    var activeIndex = Array.prototype.indexOf.call(thumbs, activeThumb);

    return activeIndex >= 0 ? activeIndex : 0;
  }

  function activateGalleryThumbAt(gallery, index) {
    if (!gallery || gallery.dataset.gfsGallerySliding === 'true') return false;

    var thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
    if (thumbs.length < 2) return false;

    var nextIndex = ((index % thumbs.length) + thumbs.length) % thumbs.length;
    activateGalleryThumb(thumbs[nextIndex]);

    gallery.dispatchEvent(
      new CustomEvent('gigiGalleryUserNavigated', {
        detail: { index: nextIndex, source: 'swipe' },
      })
    );

    return true;
  }

  function getGallerySwipeTarget(gallery, deltaX) {
    var thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
    if (thumbs.length < 2) return null;

    var direction = deltaX < 0 ? 1 : -1;
    var activeIndex = getActiveGalleryThumbIndex(gallery);
    var targetIndex = ((activeIndex + direction) % thumbs.length + thumbs.length) % thumbs.length;
    var targetThumb = thumbs[targetIndex];

    if (!targetThumb || !targetThumb.dataset.mediaSrc) return null;

    return {
      direction: direction,
      thumb: targetThumb,
      index: targetIndex,
    };
  }

  function clearGalleryDragState(state) {
    if (!state) return;

    state.incoming.remove();
    state.outgoing.classList.remove('gfs-panel__main-img--slide');
    state.outgoing.style.transform = '';
    state.outgoing.style.transition = '';
    state.stage.style.width = '';
    state.stage.style.height = '';
    delete state.gallery.dataset.gfsGalleryDragging;
  }

  function setGalleryDragTransforms(state, deltaX) {
    var clampedDelta = Math.max(-state.width, Math.min(state.width, deltaX));

    state.outgoing.style.transform = 'translateX(' + clampedDelta + 'px)';
    state.incoming.style.transform = 'translateX(' + (state.direction * state.width + clampedDelta) + 'px)';
  }

  function createGalleryDragState(gallery, deltaX) {
    if (!gallery || gallery.dataset.gfsGallerySliding === 'true') return null;

    var target = getGallerySwipeTarget(gallery, deltaX);
    var stage = gallery.querySelector('[data-gfs-gallery-stage]');
    var mainImage = gallery.querySelector('.gfs-panel__main-img:not(.gfs-panel__main-img--slide-incoming)');
    if (!target || !stage || !mainImage || mainImage.classList.contains('gfs-panel__main-img--loading')) return null;

    var imageRect = mainImage.getBoundingClientRect();
    var width = imageRect.width || stage.getBoundingClientRect().width;
    var height = imageRect.height || stage.getBoundingClientRect().height;
    if (!width || !height) return null;

    gallery.dataset.gfsGalleryDragging = 'true';
    stage.style.width = width + 'px';
    stage.style.height = height + 'px';

    mainImage.classList.add('gfs-panel__main-img--slide');
    mainImage.style.transition = 'none';
    mainImage.style.transform = 'translateX(0)';

    var fullSrc = target.thumb.dataset.mediaSrc;
    var dragSrc = galleryImagesReady.has(fullSrc) ? fullSrc : target.thumb.dataset.thumbSrc || fullSrc;
    var incoming = document.createElement('img');
    incoming.className = 'gfs-panel__main-img gfs-panel__main-img--slide gfs-panel__main-img--slide-incoming';
    incoming.src = dragSrc;
    incoming.alt = target.thumb.dataset.alt || mainImage.alt || '';
    incoming.dataset.mediaSrc = fullSrc;
    incoming.loading = 'eager';
    if (mainImage.width) incoming.width = mainImage.width;
    if (mainImage.height) incoming.height = mainImage.height;
    incoming.style.transition = 'none';
    incoming.style.transform = 'translateX(' + target.direction * width + 'px)';

    stage.appendChild(incoming);
    preloadGalleryImage(fullSrc).then(function () {
      if (incoming.isConnected && incoming.src !== fullSrc) incoming.src = fullSrc;
    });

    return {
      gallery: gallery,
      stage: stage,
      outgoing: mainImage,
      incoming: incoming,
      thumb: target.thumb,
      index: target.index,
      direction: target.direction,
      width: width,
    };
  }

  function finishGalleryDrag(state, shouldCommit) {
    if (!state) return;

    state.outgoing.style.transition = '';
    state.incoming.style.transition = '';
    state.incoming.offsetHeight;

    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;

      if (shouldCommit) {
        state.incoming.classList.remove('gfs-panel__main-img--slide', 'gfs-panel__main-img--slide-incoming');
        state.incoming.style.transform = '';
        state.incoming.style.transition = '';
        state.outgoing.remove();
        state.stage.style.width = '';
        state.stage.style.height = '';
        delete state.gallery.dataset.gfsGallerySliding;
        delete state.gallery.dataset.gfsGalleryDragging;
        applyGalleryImage(state.incoming, state.thumb, state.thumb.dataset.mediaSrc);
        state.gallery.dispatchEvent(
          new CustomEvent('gigiGalleryUserNavigated', {
            detail: { index: state.index, source: 'swipe' },
          })
        );
        var loadingBar = state.gallery.querySelector('carousel-loading-bar');
        if (loadingBar && typeof loadingBar.restartAutoplay === 'function') {
          loadingBar.restartAutoplay(state.index);
        }
        return;
      }

      clearGalleryDragState(state);
    }

    if (shouldCommit) {
      state.gallery.dataset.gfsGallerySliding = 'true';
      markGalleryThumbs(state.gallery, state.thumb);
      state.gallery.dataset.gfsActiveIndex = String(state.index);

      state.outgoing.style.transform = 'translateX(' + state.direction * -state.width + 'px)';
      state.incoming.style.transform = 'translateX(0)';
    } else {
      state.outgoing.style.transform = 'translateX(0)';
      state.incoming.style.transform = 'translateX(' + state.direction * state.width + 'px)';
    }

    state.incoming.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, GFS_GALLERY_SLIDE_MS + 80);
  }

  function activateGalleryThumb(thumb) {
    if (!thumb) return;

    var gallery = thumb.closest('[data-gfs-gallery]');
    if (!gallery) return;

    if (gallery.dataset.gfsGallerySliding === 'true') return;

    var mainImage = gallery.querySelector('.gfs-panel__main-img');
    var fullSrc = thumb.dataset.mediaSrc;
    var previewSrc = thumb.dataset.thumbSrc;
    if (!mainImage || !fullSrc) return;

    var thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
    var nextIndex = Array.prototype.indexOf.call(thumbs, thumb);
    var prevIndex = parseInt(gallery.dataset.gfsActiveIndex || '0', 10);
    var direction = getGallerySlideDirection(prevIndex, nextIndex, thumbs.length);

    markGalleryThumbs(gallery, thumb);
    gallery.dataset.gfsActiveIndex = String(nextIndex);

    if (mainImage.dataset.mediaSrc === fullSrc && !mainImage.classList.contains('gfs-panel__main-img--loading')) {
      return;
    }

    function revealFullImage() {
      slideGalleryImage(gallery, mainImage, thumb, fullSrc, direction);
    }

    if (galleryImagesReady.has(fullSrc)) {
      revealFullImage();
      return;
    }

    if (previewSrc) {
      mainImage.classList.add('gfs-panel__main-img--loading');
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = previewSrc;
    }

    preloadGalleryImage(fullSrc).then(function () {
      var activeThumb = gallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
      if (activeThumb !== thumb) return;

      revealFullImage();
    });
  }

  function bindGalleries() {
    document.querySelectorAll('[data-gfs-gallery]').forEach(function (gallery) {
      var activeThumb = gallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
      if (activeThumb) {
        var thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
        gallery.dataset.gfsActiveIndex = String(Array.prototype.indexOf.call(thumbs, activeThumb));
      }

      bindGallerySwipe(gallery);
      markGalleryStageImageState(gallery.querySelector('[data-gfs-gallery-stage]'));
    });
  }

  function isGallerySwipeEnabled(gallery) {
    return (
      gallery &&
      gallery.classList.contains('gfs-panel__gallery--with-ingredients') &&
      gallery.querySelectorAll('[data-gfs-gallery-thumb]').length > 1
    );
  }

  function isDesktopGallerySwipeEnabled(gallery) {
    return (
      isGallerySwipeEnabled(gallery) &&
      window.matchMedia &&
      window.matchMedia(GFS_DESKTOP_QUERY).matches
    );
  }

  function isInteractiveGalleryTarget(target) {
    return Boolean(
      target &&
      target.closest &&
      target.closest('a, button, input, select, textarea, [data-gigi-card-zoom]')
    );
  }

  function bindGallerySwipe(gallery) {
    if (!gallery || gallery.dataset.gfsSwipeBound === 'true') return;

    var stage = gallery.querySelector('[data-gfs-gallery-stage]');
    if (!stage || !window.PointerEvent) return;

    gallery.dataset.gfsSwipeBound = 'true';

    var startX = 0;
    var startY = 0;
    var pointerId = null;
    var tracking = false;
    var horizontalSwipe = false;
    var dragState = null;
    var documentSwipeBound = false;

    function bindDocumentSwipeEvents() {
      if (documentSwipeBound) return;

      documentSwipeBound = true;
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerCancel);
      window.addEventListener('blur', onPointerCancel);
    }

    function unbindDocumentSwipeEvents() {
      if (!documentSwipeBound) return;

      documentSwipeBound = false;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('blur', onPointerCancel);
    }

    function resetSwipe() {
      unbindDocumentSwipeEvents();
      startX = 0;
      startY = 0;
      pointerId = null;
      tracking = false;
      horizontalSwipe = false;
      dragState = null;
      stage.classList.remove('is-gfs-dragging');
    }

    stage.addEventListener('pointerdown', function (event) {
      if (!isGallerySwipeEnabled(gallery)) return;
      if (event.button > 0) return;
      if (isInteractiveGalleryTarget(event.target)) return;
      if (gallery.dataset.gfsGallerySliding === 'true' || gallery.dataset.gfsGalleryDragging === 'true') return;

      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
      tracking = true;
      horizontalSwipe = false;
      bindDocumentSwipeEvents();

      if (stage.setPointerCapture) {
        try {
          stage.setPointerCapture(pointerId);
        } catch (error) {
          // Some browsers can refuse capture during interrupted gestures.
        }
      }
    });

    function onPointerMove(event) {
      if (!tracking || event.pointerId !== pointerId) return;

      var deltaX = event.clientX - startX;
      var deltaY = event.clientY - startY;
      var absX = Math.abs(deltaX);
      var absY = Math.abs(deltaY);
      var target = absX > GFS_SWIPE_START_DISTANCE ? getGallerySwipeTarget(gallery, deltaX) : null;

      if (!horizontalSwipe && target && absX > GFS_SWIPE_START_DISTANCE && absX > absY * GFS_SWIPE_AXIS_RATIO) {
        horizontalSwipe = true;
        stage.classList.add('is-gfs-dragging');
      }

      if (!horizontalSwipe) return;

      event.preventDefault();

      if (target && (!dragState || dragState.direction !== target.direction)) {
        clearGalleryDragState(dragState);
        dragState = createGalleryDragState(gallery, deltaX);
      }

      if (dragState) setGalleryDragTransforms(dragState, deltaX);
    }

    function onPointerUp(event) {
      if (!tracking || event.pointerId !== pointerId) return;

      var deltaX = event.clientX - startX;
      var deltaY = event.clientY - startY;
      var absX = Math.abs(deltaX);
      var absY = Math.abs(deltaY);

      if (stage.releasePointerCapture) {
        try {
          stage.releasePointerCapture(pointerId);
        } catch (error) {
          // Capture may already be released after a browser-level gesture.
        }
      }

      if (absX >= GFS_SWIPE_MIN_DISTANCE && absX > absY * GFS_SWIPE_AXIS_RATIO) {
        event.preventDefault();
        if (dragState) {
          finishGalleryDrag(dragState, true);
        } else {
          activateGalleryThumbAt(gallery, getActiveGalleryThumbIndex(gallery) + (deltaX < 0 ? 1 : -1));
        }
      } else if (dragState) {
        finishGalleryDrag(dragState, false);
      }

      resetSwipe();
    }

    function onPointerCancel() {
      if (dragState) finishGalleryDrag(dragState, false);
      resetSwipe();
    }

    stage.addEventListener('dragstart', function (event) {
      if (!isDesktopGallerySwipeEnabled(gallery)) return;
      event.preventDefault();
    });
  }

  bindGalleries();

  function setGalleryMedia(galleryRoot, mediaKey) {
    if (!galleryRoot || !mediaKey) return false;

    var gallery = galleryRoot.matches && galleryRoot.matches('[data-gfs-gallery]')
      ? galleryRoot
      : galleryRoot.querySelector('[data-gfs-gallery]');
    if (!gallery) return false;

    var thumbs = gallery.querySelectorAll('[data-gfs-gallery-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].dataset.mediaKey === mediaKey) {
        activateGalleryThumb(thumbs[i]);
        return true;
      }
    }

    return false;
  }

  function activateFirstGalleryThumb(galleryRoot) {
    if (!galleryRoot) return;

    var gallery = galleryRoot.matches && galleryRoot.matches('[data-gfs-gallery]')
      ? galleryRoot
      : galleryRoot.querySelector('[data-gfs-gallery]');
    if (!gallery) return;

    var activeThumb = gallery.querySelector('[data-gfs-gallery-thumb][aria-current="true"]');
    var firstThumb = activeThumb || gallery.querySelector('[data-gfs-gallery-thumb]');
    if (firstThumb) activateGalleryThumb(firstThumb);
  }

  function setActiveProductGallery(panel, productHandle, mediaKey) {
    if (!panel || !productHandle) return;

    var productGalleries = panel.querySelectorAll('[data-gfs-product-gallery]');
    if (!productGalleries.length) {
      setGalleryMedia(panel, mediaKey);
      return;
    }

    var activeGallery = null;

    productGalleries.forEach(function (productGallery) {
      var isActive = productGallery.dataset.gfsProductGallery === productHandle;
      productGallery.hidden = !isActive;

      if (isActive) activeGallery = productGallery;
    });

    if (!activeGallery) return;

    markGalleryStageImageState(activeGallery.querySelector('[data-gfs-gallery-stage]'));

    if (mediaKey && setGalleryMedia(activeGallery, mediaKey)) return;

    activateFirstGalleryThumb(activeGallery);
  }

  var GFS_PRICE_ANIM_MS = 220;

  function cancelPriceDisplayAnimation(el) {
    if (!el) return;

    if (el._gfsPriceAnimTimeout) {
      window.clearTimeout(el._gfsPriceAnimTimeout);
      el._gfsPriceAnimTimeout = null;
    }
    el.classList.remove('gfs-panel__price--updating');
    delete el.dataset.gfsPriceAnimating;
    delete el._gfsPendingPrice;
    delete el._gfsPendingComparePrice;
    delete el._gfsPendingCanCount;
  }

  function renderPriceDisplay(el, price, comparePrice, canCount) {
    if (!el || !price) return;
    var priceOffer = el.querySelector('.gfs-panel__price-offer');
    var oldPrice = el.querySelector('.gfs-panel__price-old');
    var currentPrice = el.querySelector('.gfs-panel__price-current');
    var perCanPrice = el.querySelector('.gfs-panel__price-percan');
    var normalizedCanCount = Number.parseInt(canCount, 10);
    var numericPrice = Number.parseFloat(String(price).replace(/[^0-9.]/g, ''));
    var showPerCanPrice = normalizedCanCount > 1 && Number.isFinite(numericPrice);

    if (!priceOffer) {
      priceOffer = document.createElement('span');
      priceOffer.className = 'gfs-panel__price-offer';
      el.append(priceOffer);
    }

    if (comparePrice) {
      if (!oldPrice) {
        oldPrice = document.createElement('s');
        oldPrice.className = 'gfs-panel__price-old';
      }
      el.insertBefore(oldPrice, priceOffer);
      oldPrice.textContent = comparePrice;
    } else if (oldPrice) {
      oldPrice.remove();
    }

    if (!currentPrice) {
      currentPrice = document.createElement('span');
      currentPrice.className = 'gfs-panel__price-current';
    }

    priceOffer.prepend(currentPrice);
    currentPrice.textContent = price;

    if (showPerCanPrice) {
      if (!perCanPrice) {
        perCanPrice = document.createElement('span');
        perCanPrice.className = 'gfs-panel__price-percan';
      }
      priceOffer.append(perCanPrice);
      perCanPrice.textContent = '₹' + Math.round(numericPrice / normalizedCanCount) + ' per can';
    } else if (perCanPrice) {
      perCanPrice.remove();
    }

    el.dataset.currentPrice = price;
    el.dataset.currentComparePrice = comparePrice || '';
    el.dataset.currentCanCount = Number.isFinite(normalizedCanCount) ? String(normalizedCanCount) : '';
  }

  function animatePriceDisplay(el, nextPrice, nextComparePrice, nextCanCount) {
    if (!el || nextPrice == null) return;

    var next = String(nextPrice).trim();
    var nextCompare = nextComparePrice ? String(nextComparePrice).trim() : '';
    var nextCount = nextCanCount ? String(nextCanCount).trim() : '';
    var priceIsCurrent = el.dataset.currentPrice === next
      && (el.dataset.currentComparePrice || '') === nextCompare
      && (el.dataset.currentCanCount || '') === nextCount;

    if (priceIsCurrent) {
      cancelPriceDisplayAnimation(el);
      return;
    }

    if (gfsPrefersReducedMotion()) {
      cancelPriceDisplayAnimation(el);
      renderPriceDisplay(el, next, nextCompare, nextCount);
      return;
    }

    el._gfsPendingPrice = next;
    el._gfsPendingComparePrice = nextCompare;
    el._gfsPendingCanCount = nextCount;

    if (el.dataset.gfsPriceAnimating === 'true') return;

    function runPriceCycle() {
      var target = el._gfsPendingPrice;
      var targetCompare = el._gfsPendingComparePrice || '';
      var targetCanCount = el._gfsPendingCanCount || '';
      delete el._gfsPendingPrice;
      delete el._gfsPendingComparePrice;
      delete el._gfsPendingCanCount;

      if (!target || (el.dataset.currentPrice === target
        && (el.dataset.currentComparePrice || '') === targetCompare
        && (el.dataset.currentCanCount || '') === targetCanCount)) {
        delete el.dataset.gfsPriceAnimating;
        el.classList.remove('gfs-panel__price--updating');
        return;
      }

      el.dataset.gfsPriceAnimating = 'true';
      el.classList.add('gfs-panel__price--updating');

      el._gfsPriceAnimTimeout = window.setTimeout(function () {
        renderPriceDisplay(el, target, targetCompare, targetCanCount);
        el.classList.remove('gfs-panel__price--updating');

        el._gfsPriceAnimTimeout = window.setTimeout(function () {
          delete el._gfsPriceAnimTimeout;
          delete el.dataset.gfsPriceAnimating;

          if (el._gfsPendingPrice) runPriceCycle();
        }, GFS_PRICE_ANIM_MS);
      }, GFS_PRICE_ANIM_MS);
    }

    runPriceCycle();
  }

  function syncFlavorSelection(input, animatePrice) {
    var selector = input.closest('[data-flavor]');
    var panel = input.closest('.gfs-panel');
    if (!selector || !panel) return;

    var flavor = selector.dataset.flavor;
    var priceDisplay = panel.querySelector('[data-price-display="' + flavor + '"]');
    var mobileDetailsButton = panel.querySelector('.gfs-panel__mobile-details-button');
    var isFourPack = input.id && input.id.slice(-2) === '-4';
    var activeTabIndex = input.dataset.gfsTabIndex;
    var form = input.closest('form');
    var submitButton = form && form.querySelector('.gigi-add-to-cart-button[type="submit"]');
    var submitLabel = submitButton && submitButton.querySelector('[data-gigi-add-to-cart-label]');

    panel.classList.toggle('gfs-panel--four-pack-active', isFourPack);
    if (activeTabIndex != null) selector.style.setProperty('--gfs-tabs-active-index', activeTabIndex);

    if (submitButton && submitLabel) {
      submitButton.disabled = input.disabled;
      if (input.disabled) {
        submitButton.setAttribute('aria-disabled', 'true');
      } else {
        submitButton.removeAttribute('aria-disabled');
      }
      submitLabel.textContent = input.disabled ? 'Sold out' : 'Add to Cart';
    }

    selector.querySelectorAll('.gfs-tabs__input, .gfs-qty__input').forEach(function (option) {
      var label = option.nextElementSibling;
      if (!label || !label.classList.contains('gfs-tabs__tab')) return;

      var isSelected = option === input;
      label.classList.toggle('gfs-tabs__tab--active', isSelected);
    });

    if (priceDisplay) {
      var nextPrice = String(input.dataset.price || '').trim();
      var nextComparePrice = input.dataset.comparePrice ? String(input.dataset.comparePrice).trim() : '';
      var nextCanCount = input.dataset.canCount ? String(input.dataset.canCount).trim() : '';
      var priceIsCurrent = priceDisplay.dataset.currentPrice === nextPrice
        && (priceDisplay.dataset.currentComparePrice || '') === nextComparePrice
        && (priceDisplay.dataset.currentCanCount || '') === nextCanCount;

      if (priceIsCurrent) {
        cancelPriceDisplayAnimation(priceDisplay);
      } else if (animatePrice === false) {
        renderPriceDisplay(priceDisplay, nextPrice, nextComparePrice, nextCanCount);
      } else {
        animatePriceDisplay(priceDisplay, nextPrice, nextComparePrice, nextCanCount);
      }
    }
    if (mobileDetailsButton && input.dataset.productUrl) mobileDetailsButton.href = input.dataset.productUrl;

    if (input.dataset.productHandle) {
      setActiveProductGallery(panel, input.dataset.productHandle, input.dataset.mediaKey);
    } else if (input.dataset.mediaKey) {
      setGalleryMedia(panel, input.dataset.mediaKey);
    }
  }

  if (window.gigiFlavorSelectorGalleryHandler) {
    document.removeEventListener('click', window.gigiFlavorSelectorGalleryHandler);
  }

  window.gigiFlavorSelectorGalleryHandler = function (event) {
    var target = event.target.nodeType === 1 ? event.target : event.target.parentElement;
    var thumb = target && target.closest('[data-gfs-gallery-thumb]');
    if (!thumb) return;

    event.preventDefault();
    activateGalleryThumb(thumb);
  };

  document.addEventListener('click', window.gigiFlavorSelectorGalleryHandler);

  if (window.gigiFlavorSelectorChangeHandler) {
    document.removeEventListener('change', window.gigiFlavorSelectorChangeHandler);
  }

  window.gigiFlavorSelectorChangeHandler = function (event) {
    var target = event.target.nodeType === 1 ? event.target : event.target.parentElement;
    if (!target || !target.matches('.gfs-tabs__input, .gfs-qty__input') || target.disabled || !target.checked) return;

    syncFlavorSelection(target);
  };

  document.addEventListener('change', window.gigiFlavorSelectorChangeHandler);

  document.querySelectorAll('.gfs-tabs__input:checked, .gfs-qty__input:checked').forEach(function (input) {
    syncFlavorSelection(input, false);
  });

  function gfsFizzHexToRgb(hex) {
    var normalized = String(hex || '#000000').replace('#', '');
    var value = parseInt(normalized, 16);

    return {
      r: (value >> 16) & 255,
      g: (value >> 8) & 255,
      b: value & 255
    };
  }

  function gfsFizzRgba(hex, alpha) {
    var rgb = gfsFizzHexToRgb(hex);
    return 'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ', ' + alpha + ')';
  }

  function createGfsFizzIndicator(indicator) {
    if (!indicator || indicator._gfsFizzIndicator) return;

    var canvas = indicator.querySelector('.gfs-tabs__fizz-canvas');
    var selector = indicator.closest('[data-gfs-tabs]');
    var context = canvas && canvas.getContext && canvas.getContext('2d');
    if (!canvas || !context || !selector) return;

    var flavor = selector.dataset.flavor;
    var palette = flavor === 'pc'
      ? { base: '#FEDD4F', deep: '#F8C83A', light: '#FEF278' }
      : { base: '#CFEA4A', deep: '#AEDD18', light: '#FEF278' };
    var reducedMotionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    var state = {
      active: true,
      bubbles: [],
      dpr: 1,
      height: 0,
      hover: false,
      lastFrame: 0,
      pointerX: 0.5,
      pointerY: 0.5,
      pops: [],
      raf: null,
      running: false,
      visible: true,
      width: 0
    };

    function randomBubble() {
      return {
        x: Math.random(),
        y: Math.random(),
        r: 0.9 + Math.random() * 2.6,
        speed: 0.0018 + Math.random() * 0.0032,
        drift: 0.12 + Math.random() * 0.4,
        phase: Math.random() * Math.PI * 2,
        alpha: 0.28 + Math.random() * 0.5
      };
    }

    function resetBubbleFromBottom(bubble) {
      var nextBubble = randomBubble();
      bubble.x = nextBubble.x;
      bubble.y = 1.04 + Math.random() * 0.2;
      bubble.r = nextBubble.r;
      bubble.speed = nextBubble.speed;
      bubble.drift = nextBubble.drift;
      bubble.phase = nextBubble.phase;
      bubble.alpha = nextBubble.alpha;
    }

    function resize() {
      var rect = indicator.getBoundingClientRect();
      var width = Math.max(1, Math.round(rect.width));
      var height = Math.max(1, Math.round(rect.height));
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      if (state.width === width && state.height === height && state.dpr === dpr) return;

      state.width = width;
      state.height = height;
      state.dpr = dpr;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      var bubbleCount = Math.max(14, Math.round(width / 6));
      while (state.bubbles.length < bubbleCount) state.bubbles.push(randomBubble());
      if (state.bubbles.length > bubbleCount) state.bubbles.length = bubbleCount;
    }

    function drawLiquidBackground(time) {
      var width = state.width;
      var height = state.height;
      var gradient = context.createLinearGradient(0, 0, 0, height);
      var pulse = Math.sin(time * 0.0012) * 0.08;

      gradient.addColorStop(0, gfsFizzRgba(palette.light, 0.92));
      gradient.addColorStop(0.2 + pulse, gfsFizzRgba(palette.base, 0.86));
      gradient.addColorStop(0.7, gfsFizzRgba(palette.base, 0.98));
      gradient.addColorStop(1, gfsFizzRgba(palette.deep, 0.72));

      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      context.globalCompositeOperation = 'screen';
      context.lineWidth = 1.2;
      for (var i = 0; i < 4; i++) {
        var y = height * (0.22 + i * 0.18) + Math.sin(time * 0.0014 + i) * 4;
        context.beginPath();
        context.moveTo(-8, y);
        context.bezierCurveTo(width * 0.28, y - 10, width * 0.58, y + 12, width + 8, y - 4);
        context.strokeStyle = gfsFizzRgba(palette.light, i === 0 ? 0.3 : 0.15);
        context.stroke();
      }
      context.globalCompositeOperation = 'source-over';

      var lowerGlow = context.createLinearGradient(0, height * 0.62, 0, height);
      lowerGlow.addColorStop(0, gfsFizzRgba(palette.base, 0));
      lowerGlow.addColorStop(0.58, gfsFizzRgba(palette.base, 0.2));
      lowerGlow.addColorStop(1, gfsFizzRgba(palette.deep, 0.26));
      context.fillStyle = lowerGlow;
      context.fillRect(0, height * 0.62, width, height * 0.38);
    }

    function drawMeniscus(time) {
      var width = state.width;
      var height = state.height;
      var y = height * 0.2 + Math.sin(time * 0.0015) * 1.6;

      context.beginPath();
      context.moveTo(4, y);
      context.bezierCurveTo(width * 0.28, y - 5, width * 0.68, y + 5, width - 4, y - 1);
      context.strokeStyle = gfsFizzRgba('#FFFFFF', 0.44);
      context.lineWidth = 1.4;
      context.stroke();

      context.beginPath();
      context.ellipse(width * 0.22, height * 0.18, width * 0.34, height * 0.28, -0.18, 0, Math.PI * 2);
      context.fillStyle = gfsFizzRgba('#FFFFFF', 0.12);
      context.fill();
    }

    function drawBubbles(time, delta) {
      var width = state.width;
      var height = state.height;
      var lift = (state.active ? 1.18 : 1) * (state.hover ? 1.14 : 1);
      var pointerPull = (state.pointerX - 0.5) * 4;

      for (var i = 0; i < state.bubbles.length; i++) {
        var bubble = state.bubbles[i];
        bubble.y -= bubble.speed * lift * delta;
        if (bubble.y * height + bubble.r < -4) {
          state.bubbles[i] = randomBubble();
          state.bubbles[i].y = 1.05 + Math.random() * 0.18;
          bubble = state.bubbles[i];
        }

        var x = bubble.x * width + Math.sin(time * 0.0018 + bubble.phase) * bubble.drift * 9 + pointerPull * bubble.drift;
        var y = bubble.y * height;
        var radius = bubble.r * (state.active ? 1.08 : 1);

        context.beginPath();
        context.arc(x, y, radius, 0, Math.PI * 2);
        context.fillStyle = gfsFizzRgba('#FFFFFF', bubble.alpha * 0.36);
        context.fill();
        context.lineWidth = 0.8;
        context.strokeStyle = gfsFizzRgba('#FFFFFF', bubble.alpha * 0.78);
        context.stroke();

        context.beginPath();
        context.arc(x - radius * 0.32, y - radius * 0.3, Math.max(0.6, radius * 0.22), 0, Math.PI * 2);
        context.fillStyle = gfsFizzRgba('#FFFFFF', bubble.alpha * 0.82);
        context.fill();
      }
    }

    function drawBubblePops(time) {
      for (var i = state.pops.length - 1; i >= 0; i--) {
        var pop = state.pops[i];
        var age = time - pop.startedAt;
        if (age < 0) continue;
        if (age > 360) {
          state.pops.splice(i, 1);
          continue;
        }

        var progress = age / 360;
        var alpha = 1 - progress;
        var radius = pop.r + progress * 7;

        context.beginPath();
        context.arc(pop.x, pop.y, radius, 0, Math.PI * 2);
        context.strokeStyle = gfsFizzRgba('#FFFFFF', alpha * 0.72);
        context.lineWidth = 1;
        context.stroke();

        context.beginPath();
        context.arc(pop.x - radius * 0.38, pop.y - radius * 0.28, Math.max(0.5, 1.8 * alpha), 0, Math.PI * 2);
        context.fillStyle = gfsFizzRgba('#FFFFFF', alpha * 0.6);
        context.fill();
      }
    }

    function popSomeBubbles() {
      if (!state.bubbles.length || !state.width || !state.height) return;

      var time = performance.now();
      var popCount = Math.min(5, Math.max(2, Math.round(state.bubbles.length * 0.18)));
      var startIndex = Math.floor(Math.random() * state.bubbles.length);

      for (var i = 0; i < popCount; i++) {
        var bubble = state.bubbles[(startIndex + i * 3) % state.bubbles.length];
        var x = bubble.x * state.width + Math.sin(time * 0.0018 + bubble.phase) * bubble.drift * 9;
        var y = bubble.y * state.height;

        if (y < 2 || y > state.height - 2) continue;

        state.pops.push({
          x: x,
          y: y,
          r: bubble.r,
          startedAt: time + i * 24
        });
        resetBubbleFromBottom(bubble);
      }
    }

    function drawFrame(time) {
      resize();
      state.active = true;

      var delta = state.lastFrame ? Math.min(2.2, (time - state.lastFrame) / 16.66) : 1;
      state.lastFrame = time;

      context.clearRect(0, 0, state.width, state.height);
      drawLiquidBackground(time);
      drawBubbles(time, delta);
      drawBubblePops(time);
      drawMeniscus(time);

      if (state.running && !gfsPrefersReducedMotion()) {
        state.raf = window.requestAnimationFrame(drawFrame);
      }
    }

    function stop() {
      state.running = false;
      if (state.raf) window.cancelAnimationFrame(state.raf);
      state.raf = null;
    }

    function start() {
      if (state.running || !state.visible) return;
      state.running = true;
      state.lastFrame = 0;
      state.raf = window.requestAnimationFrame(drawFrame);
    }

    function renderStatic() {
      stop();
      drawFrame(400);
    }

    function handlePointerMove(event) {
      var rect = indicator.getBoundingClientRect();
      state.pointerX = rect.width ? (event.clientX - rect.left) / rect.width : 0.5;
      state.pointerY = rect.height ? (event.clientY - rect.top) / rect.height : 0.5;
      indicator.style.setProperty('--gfs-fizz-pointer-y', Math.max(0, Math.min(1, state.pointerY)));
    }

    function getTabInputFromEvent(event) {
      var target = event.target.nodeType === 1 ? event.target : event.target.parentElement;
      var tabLabel = target && target.closest('.gfs-tabs__tab');
      if (!tabLabel || !selector.contains(tabLabel)) return null;

      var input = tabLabel.previousElementSibling;
      if (!input || input.disabled || !input.matches('.gfs-tabs__input, .gfs-qty__input')) return null;

      return input;
    }

    function syncIndicatorToCheckedTab() {
      var checkedInput = selector.querySelector('.gfs-tabs__input:checked, .gfs-qty__input:checked');
      if (checkedInput && checkedInput.dataset.gfsTabIndex != null) {
        selector.style.setProperty('--gfs-tabs-active-index', checkedInput.dataset.gfsTabIndex);
      }
    }

    function setIndicatorPressed(isPressed) {
      indicator.style.setProperty('--gfs-tabs-press-scale', isPressed ? '0.96' : '1');
      indicator.style.setProperty('--gfs-tabs-shadow-y', isPressed ? '0.12rem' : '0.4rem');
      indicator.style.setProperty('--gfs-tabs-shadow-blur', isPressed ? '0.55rem' : '1.2rem');
      indicator.style.setProperty('--gfs-tabs-shadow-opacity', isPressed ? '0.1' : '0.18');
    }

    selector.addEventListener('pointerenter', function () {
      state.hover = true;
      start();
    });

    selector.addEventListener('pointermove', handlePointerMove);

    selector.addEventListener('pointerleave', function () {
      state.hover = false;
      state.pointerX = 0.5;
      state.pointerY = 0.5;
      indicator.style.setProperty('--gfs-fizz-pointer-y', '0.5');
      setIndicatorPressed(false);
      syncIndicatorToCheckedTab();
    });

    selector.addEventListener('pointerdown', function (event) {
      var input = getTabInputFromEvent(event);
      if (!input) return;

      if (input.dataset.gfsTabIndex != null) {
        selector.style.setProperty('--gfs-tabs-active-index', input.dataset.gfsTabIndex);
      }

      setIndicatorPressed(true);
      popSomeBubbles();
      start();
    });

    selector.addEventListener('pointerup', function () {
      setIndicatorPressed(false);
    });

    selector.addEventListener('pointercancel', function () {
      setIndicatorPressed(false);
      syncIndicatorToCheckedTab();
    });

    selector.addEventListener('change', function () {
      state.active = true;
      setIndicatorPressed(false);
      start();
    });

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(function () {
        resize();
        if (gfsPrefersReducedMotion()) renderStatic();
      });
      state.resizeObserver.observe(indicator);
    } else {
      window.addEventListener('resize', resize);
      state.removeResizeFallback = function () {
        window.removeEventListener('resize', resize);
      };
    }

    if ('IntersectionObserver' in window) {
      state.intersectionObserver = new IntersectionObserver(function (entries) {
        state.visible = entries[0] ? entries[0].isIntersecting : true;
        if (state.visible) {
          if (gfsPrefersReducedMotion()) renderStatic();
          else start();
        } else {
          stop();
        }
      }, { threshold: 0.01 });
      state.intersectionObserver.observe(selector);
    }

    indicator._gfsFizzIndicator = {
      destroy: function () {
        stop();
        if (state.resizeObserver) state.resizeObserver.disconnect();
        if (state.intersectionObserver) state.intersectionObserver.disconnect();
        if (state.removeResizeFallback) state.removeResizeFallback();
        indicator._gfsFizzIndicator = null;
      }
    };

    resize();
    if (gfsPrefersReducedMotion()) renderStatic();
    else start();

    if (reducedMotionQuery && reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener('change', function () {
        if (gfsPrefersReducedMotion()) renderStatic();
        else start();
      });
    }
  }

  function initGfsFizzIndicators(root) {
    var scope = root || document;
    scope.querySelectorAll('.gfs [data-gfs-fizz-indicator]').forEach(createGfsFizzIndicator);
  }

  initGfsFizzIndicators(document);

  if (window.gigiFlavorSelectorFizzSectionHandler) {
    document.removeEventListener('shopify:section:load', window.gigiFlavorSelectorFizzSectionHandler);
  }

  window.gigiFlavorSelectorFizzSectionHandler = function (event) {
    initGfsFizzIndicators(event.target);
  };

  document.addEventListener('shopify:section:load', window.gigiFlavorSelectorFizzSectionHandler);

  var GFS_INGREDIENTS_DIALOG_MS = 220;

  function openIngredientsDialog(button) {
    var targetId = button && button.dataset.gfsIngredientsTarget;
    var dialog = targetId ? document.getElementById(targetId) : null;
    if (!dialog) return;

    dialog._gfsIngredientsOpener = button;
    dialog.classList.remove('is-closing', 'is-open');
    document.body.classList.add('gfs-ingredients-modal-open');

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    if (gfsPrefersReducedMotion()) {
      dialog.classList.add('is-open');
    } else {
      dialog.offsetHeight;
      window.requestAnimationFrame(function () {
        if (!dialog.open || dialog.classList.contains('is-closing')) return;
        dialog.classList.add('is-open');
      });
    }

    var closeButton = dialog.querySelector('[data-gfs-ingredients-close]');
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  function finishIngredientsDialogClose(dialog) {
    if (!dialog) return;

    dialog.classList.remove('is-closing', 'is-open');

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }

    document.body.classList.remove('gfs-ingredients-modal-open');

    if (dialog._gfsIngredientsOpener) {
      restoreFocusAfterModal(dialog._gfsIngredientsOpener);
      dialog._gfsIngredientsOpener = null;
    }
  }

  function closeIngredientsDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;

    if (gfsPrefersReducedMotion()) {
      finishIngredientsDialogClose(dialog);
      return;
    }

    dialog.classList.remove('is-open');
    dialog.classList.add('is-closing');
    window.setTimeout(function () {
      finishIngredientsDialogClose(dialog);
    }, GFS_INGREDIENTS_DIALOG_MS);
  }

  function setIngredientsAccordion(accordion, shouldOpen) {
    var trigger = accordion && accordion.querySelector('[data-gfs-ingredients-trigger]');
    var panel = accordion && accordion.querySelector('[data-gfs-ingredients-panel]');
    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    panel.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
    accordion.classList.toggle('is-open', shouldOpen);

    if (shouldOpen) {
      panel.hidden = false;
      panel.offsetHeight;
      window.requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
      return;
    }

    panel.classList.remove('is-open');

    if (gfsPrefersReducedMotion()) {
      panel.hidden = true;
      return;
    }

    panel.addEventListener('transitionend', function (event) {
      if (event.target !== panel || accordion.classList.contains('is-open')) return;
      panel.hidden = true;
    }, { once: true });
  }

  if (window.gigiFlavorIngredientsClickHandler) {
    document.removeEventListener('click', window.gigiFlavorIngredientsClickHandler);
  }

  window.gigiFlavorIngredientsClickHandler = function (event) {
    var target = event.target.nodeType === 1 ? event.target : event.target.parentElement;
    var openButton = target && target.closest('[data-gfs-ingredients-open]');
    if (openButton) {
      event.preventDefault();
      openIngredientsDialog(openButton);
      return;
    }

    var closeButton = target && target.closest('[data-gfs-ingredients-close]');
    if (closeButton) {
      event.preventDefault();
      closeIngredientsDialog(closeButton.closest('[data-gfs-ingredients-dialog]'));
      return;
    }

    var trigger = target && target.closest('[data-gfs-ingredients-trigger]');
    if (!trigger) return;

    var accordion = trigger.closest('[data-gfs-ingredients-accordion]');
    if (!accordion) return;

    event.preventDefault();
    setIngredientsAccordion(accordion, trigger.getAttribute('aria-expanded') !== 'true');
  };

  document.addEventListener('click', window.gigiFlavorIngredientsClickHandler);

  document.querySelectorAll('[data-gfs-ingredients-dialog]').forEach(function (dialog) {
    if (dialog.dataset.gfsIngredientsBound === 'true') return;

    dialog.dataset.gfsIngredientsBound = 'true';

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeIngredientsDialog(dialog);
    });

    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeIngredientsDialog(dialog);
    });

    dialog.addEventListener('close', function () {
      dialog.classList.remove('is-closing', 'is-open');
      document.body.classList.remove('gfs-ingredients-modal-open');
    });
  });

  var shopGigiScrollFrame = null;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function normalizePath(pathname) {
    var normalized = pathname.replace(/\/$/, '');
    return normalized || '/';
  }

  function getShopGigiScrollTop(target) {
    var styles = window.getComputedStyle(target);
    var scrollMarginTop = parseFloat(styles.scrollMarginTop) || 0;
    return target.getBoundingClientRect().top + window.pageYOffset - scrollMarginTop;
  }

  function easeInOutCubic(progress) {
    return progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
  }

  function scrollToShopGigi(targetTop) {
    var startTop = window.pageYOffset;
    var distance = targetTop - startTop;
    var duration = Math.min(1100, Math.max(520, Math.abs(distance) * 0.55));
    var startTime = null;

    if (shopGigiScrollFrame) {
      window.cancelAnimationFrame(shopGigiScrollFrame);
    }

    function step(timestamp) {
      if (startTime === null) startTime = timestamp;

      var progress = Math.min((timestamp - startTime) / duration, 1);
      var easedProgress = easeInOutCubic(progress);

      window.scrollTo(0, startTop + distance * easedProgress);

      if (progress < 1) {
        shopGigiScrollFrame = window.requestAnimationFrame(step);
      } else {
        shopGigiScrollFrame = null;
      }
    }

    shopGigiScrollFrame = window.requestAnimationFrame(step);
  }

  if (window.gigiShopGigiScrollHandler) {
    document.removeEventListener('click', window.gigiShopGigiScrollHandler);
  }

  window.gigiShopGigiScrollHandler = function (event) {
    var clickedElement = event.target.nodeType === 1 ? event.target : event.target.parentElement;
    var link = clickedElement && clickedElement.closest('a[href*="#shop-gigi"]');
    if (!link) return;

    var url;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
    } catch (error) {
      return;
    }

    if (url.hash !== '#shop-gigi') return;
    if (normalizePath(url.pathname) !== normalizePath(window.location.pathname)) return;

    var target = document.getElementById('shop-gigi');
    if (!target) return;

    event.preventDefault();

    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', url.hash);
    }

    var targetTop = getShopGigiScrollTop(target);

    if (prefersReducedMotion()) {
      window.scrollTo(0, targetTop);
      return;
    }

    scrollToShopGigi(targetTop);
  };

  document.addEventListener('click', window.gigiShopGigiScrollHandler);

})();
